import { AfterViewInit, Component, OnInit, ViewChild, inject } from '@angular/core';
import { interval, merge, Subscription, tap } from 'rxjs';
import { ReportsListDataSource } from './reports-list.datasource';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { ReportStatusEnum } from '../../models/enums/ReportStatusEnum';
import { ReportService } from '../../data/report.service';
import { SnackBarService } from '../../shared/services/snack-bar.service';
import { Messages } from '../../common/enums/messages';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { EmployerSelectionModalComponent } from '../employer-selection-modal/employer-selection-modal.component';
import { ReportTypeEnum } from '../../models/enums/report-type';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-reports-list',
  templateUrl: './reports-list.component.html',
  styleUrl: './reports-list.component.scss',
  standalone: false
})

export class ReportsListComponent implements OnInit, AfterViewInit  {
  dataSource = inject(ReportsListDataSource);
  private translateService = inject(TranslateService);
  private reportService = inject(ReportService);
  private snackbarService = inject(SnackBarService);
  private dialog = inject(MatDialog);

  public readonly reportStatusEnum = ReportStatusEnum;
  private subscription: Subscription;
  hasAccessToEmployeesAssignedToObjectsReport : boolean = false;
  hasAccessToLimit540Usage: boolean = false;

  displayedColumns: string[] = [
    'Name',
    'ReportStatusName',
    'GeneratedDate',
    'DownloadedDate',
    'Actions'
  ];

  readonly defaultPage: number = 1;
  readonly defaultPageSize: number = 10;
  readonly defaultSortColumn: string = 'GeneratedDate';
  readonly defaultSortDirection: string = 'desc';

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor() { }

  ngOnInit() {
    this.getAccessedReportTypes();
  }

  ngAfterViewInit(): void {
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.translateService.onLangChange.subscribe(() => (this.paginator.pageIndex = 0));

    this.subscription = merge(this.sort.sortChange, this.paginator.page, this.translateService.onLangChange, interval(2000))
      .pipe(tap(() => this.fetchReports()))
      .subscribe();

    this.dataSource.reportsSubject.next({
      Page: this.defaultPage,
      PageSize: this.defaultPageSize,
      SortingField: this.defaultSortColumn,
      SortingDirection: this.defaultSortDirection
    });
  }

  onSortChange() {
    this.fetchReports();
  }

  private getAccessedReportTypes() {
    this.reportService.getAccessedReportTypes().subscribe((res) => {
      const availableReportIds = new Set(res.map(item => item.Id));

      this.hasAccessToEmployeesAssignedToObjectsReport = availableReportIds.has(ReportTypeEnum.EmployeesAssignedToObjectsReport);
      this.hasAccessToLimit540Usage = availableReportIds.has(ReportTypeEnum.Limit540Usage);
    });
  }

  onGenerateEmployeesAssignedToObjectsReport() {
    this.reportService.generateReport(ReportTypeEnum.EmployeesAssignedToObjectsReport)
      .subscribe({
        error: (error: HttpErrorResponse) => this.snackbarService.openErrorSnackBar(error.error),
        next: () => {
          this.snackbarService.openSuccessSnackBar(Messages.StartedReportGeneration);
          this.fetchReports();
        }
      });
  }

  onGenerateLimit540UsageReport() {
    const dialogRef = this.dialog.open(EmployerSelectionModalComponent, {
      width: '500px',
      data: {}
    });

    dialogRef.afterClosed().subscribe((selectedEmployers: number[]) => {
      if (selectedEmployers && selectedEmployers.length > 0) {
        this.reportService.generateReport(ReportTypeEnum.Limit540Usage, { employerIds: selectedEmployers })
          .subscribe({
            error: (error: HttpErrorResponse) => this.snackbarService.openErrorSnackBar(error.error),
            next: () => {
              this.snackbarService.openSuccessSnackBar(Messages.StartedReportGeneration);
              this.fetchReports();
            }
          });
      }
    });
  }

  onDeleteReport(reportId: number) {
    this.reportService.deleteReport(reportId)
      .subscribe({
        error: () => this.snackbarService.openErrorSnackBar(Messages.SomethingWentWrong),
        complete: () => {
          this.snackbarService.openSuccessSnackBar(Messages.SuccessfullyDeletedReport);
          this.fetchReports();
        }
      });
  }

  onDownloadReport(reportId: number, date: Date, reportType: ReportTypeEnum) {
    const datepipe: DatePipe = new DatePipe('en-US');
    let reportNameKey = '';

    switch (reportType) {
      case ReportTypeEnum.EmployeesAssignedToObjectsReport:
        reportNameKey = 'reportFileNames.employeesAssignedToMyObjects';
        break;
      case ReportTypeEnum.Limit540Usage:
        reportNameKey = 'reportFileNames.limit540Usage';
        break;
    }

    this.reportService.getReport(reportId,
      this.translateService.instant(reportNameKey, { date: datepipe.transform(date, 'dd_MM_yyyy HH:mm') }))
      .subscribe({
        error: () => this.snackbarService.openErrorSnackBar(Messages.SomethingWentWrong),
        complete: () => this.fetchReports()
      });
  }

  private fetchReports(): void {
    this.dataSource.reportsSubject.next({
      Page: this.paginator.pageIndex + 1,
      PageSize: this.paginator.pageSize ?? this.defaultPageSize,
      SortingField: this.sort?.active ?? this.defaultSortColumn,
      SortingDirection: this.sort?.direction ?? this.defaultSortDirection
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
